import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';

function AppErrorFallback({ error }: { error?: Error }): React.ReactElement {
  useEffect(() => {
    if (error) {
      Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div className="max-w-xl mx-auto my-8 p-6 border border-red-200 rounded-lg bg-white shadow-md">
      <div className="text-center mb-4">
        <svg
          className="w-16 h-16 text-red-500 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-3 text-center">Something went wrong</h2>
      <p className="text-gray-600 mb-6 text-center">
        We&apos;re sorry, something went wrong. Please try reloading the page.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          Reload
        </button>
        <a
          href="mailto:support@edgetechnologies.com.au?subject=App Error"
          className="px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200 transition text-center focus:outline-none focus:ring-2 focus:ring-gray-300"
        >
          Contact Support
        </a>
      </div>
    </div>
  );
}

interface AppErrorBoundaryProps {
  children: React.ReactNode;
}

/**
 * Sentry ErrorBoundary wrapping the app. Reports to Sentry and shows a clean fallback.
 */
export function AppErrorBoundary({ children }: AppErrorBoundaryProps): React.ReactElement {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => <AppErrorFallback error={error} />}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
